import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { colors, font, type as ty, layout } from '../theme/tokens';
import {
  WINDOW_DAYS, PERIOD_KEYS, PeriodKey, PeriodSelection,
  isoForIndex, indexForIso,
} from '../data/period';
import { useI18n } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Reporting-period selector: per day, per week, per month, or a free range.
//
// Shared by the fleet overview (01) and the maintenance tab (05) so both read
// the same figures for the same window.
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  sel: PeriodSelection;
  onChange: (s: PeriodSelection) => void;
  /** Active-segment colour. Lets a tab use its own brand accent. */
  accent?: string;
  accentDeep?: string;
  /** Extra note shown under the control, e.g. how many days are summed. */
  note?: string;
};

const PeriodSwitch: React.FC<Props> = ({
  sel, onChange,
  accent = colors.blue,
  accentDeep = colors.blueDeep,
  note,
}) => {
  const { t } = useI18n();

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mr: 0.5 }}>
          {t('period.label')}
        </Typography>

        <Box sx={{
          display: 'flex',
          border: `1px solid ${colors.border}`,
          borderRadius: layout.radius,
          overflow: 'hidden',
        }}>
          {PERIOD_KEYS.map((k: PeriodKey) => {
            const active = sel.key === k;
            return (
              <Button
                key={k}
                onClick={() => onChange({ ...sel, key: k })}
                disableRipple
                aria-pressed={active}
                sx={{
                  px: 1.75,
                  py: 0.6,
                  minWidth: 0,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontFamily: font.sans,
                  fontSize: '12px',
                  fontWeight: active ? 700 : 500,
                  color: active ? colors.onDark : colors.inkMuted,
                  backgroundColor: active ? accent : 'transparent',
                  borderRight: `1px solid ${colors.border}`,
                  '&:last-of-type': { borderRight: 'none' },
                  '&:hover': {
                    backgroundColor: active ? accentDeep : colors.blueWash,
                  },
                }}
              >
                {t(`period.${k}`)}
              </Button>
            );
          })}
        </Box>

        {sel.key === 'range' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {(['from', 'to'] as const).map(which => (
              <Box key={which} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkFaint }}>
                  {t(`period.${which}`)}
                </Typography>
                <Box
                  component="input"
                  type="date"
                  min={isoForIndex(0)}
                  max={isoForIndex(WINDOW_DAYS - 1)}
                  value={isoForIndex(which === 'from' ? sel.fromIndex : sel.toIndex)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const i = indexForIso(e.target.value);
                    onChange(which === 'from' ? { ...sel, fromIndex: i } : { ...sel, toIndex: i });
                  }}
                  sx={{
                    fontFamily: font.mono,
                    fontSize: '11px',
                    color: colors.ink,
                    px: 0.75,
                    py: 0.5,
                    border: `1px solid ${colors.border}`,
                    borderRadius: layout.radius,
                    backgroundColor: colors.bg,
                    outline: 'none',
                    '&:focus': { borderColor: accent },
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {note && (
        <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 1 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
};

export default PeriodSwitch;
